import { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "@/integrations/firebase/firestore";
import { useAuth } from "@/contexts/AuthContext";
import { logError } from "@/shared/services/ErrorReporting";
import { toast } from "@/utils/toast";
import { JobStatus, IJob } from "./useJobs";

export class JobRequest implements IJob {
  public jobRequestId: string;

  public jobId: string;

  public userId: string;

  public manuscriptUri: string;

  public status: JobStatus;

  public uploadedAt: Date;

  public summaryId: string;

  public desiredWordCount: number;

  public outputLanguage: string;

  public markedForDeletion: boolean = true;

  public displayStatus: JobStatus;

  get fileName(): string {
    return this.manuscriptUri?.split("/")?.pop() || "";
  }

  constructor(
    jobRequestId: string,
    jobId: string,
    userId: string,
    manuscriptUri: string,
    status: JobStatus,
    uploadedAt: Date,
    summaryId: string,
    desiredWordCount: number,
    outputLanguage: string,
  ) {
    this.jobRequestId = jobRequestId;
    this.jobId = jobId;
    this.userId = userId;
    this.manuscriptUri = manuscriptUri;
    this.status = status;
    this.uploadedAt = uploadedAt;
    this.summaryId = summaryId;
    this.desiredWordCount = desiredWordCount;
    this.outputLanguage = outputLanguage;
    this.displayStatus = status;
  }
}

export const useJobRequests = (limitCount: number) => {
  const { authUser } = useAuth();
  const [jobRequests, setJobRequests] = useState<IJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (error) {
      logError(error);
      toast.error(error.message);
      setError(null);
    }
  }, [error]);

  useEffect(() => {
    if (!authUser) {
      setIsLoading(false);
      return;
    }

    const q = query(
      collection(db, "job_requests"),
      where("user_id", "==", authUser.uid),
      orderBy("uploaded_at", "desc"),
      limit(limitCount),
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const jobsData: IJob[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          jobsData.push(
            new JobRequest(
              doc.id,
              data.job_id,
              data.user_id,
              data.manuscript_uri,
              JobStatus[data.status as keyof typeof JobStatus],
              data.uploaded_at?.toDate() || new Date(),
              data.summary_id,
              data.desired_word_count || 2500,
              data.output_language || "en",
            ),
          );
        });
        setJobRequests(jobsData);
        setIsLoading(false);
      },
      (err) => {
        setError(err);
        setIsLoading(false);
      },
    );

    return () => unsubscribe();
  }, [authUser, limitCount]);

  return { jobRequests, isLoading, error };
};
